/**
 * LogSanitizer - Handles serialization and circular reference removal
 * for complex objects being logged, especially for remote transport.
 */

import {
  isSensitiveLogKey,
  redactLogString,
  REDACTED_LOG_VALUE,
} from '../../shared/logging/LogRedaction';

export class LogSanitizer {
  public static safeStringify(obj: unknown): string {
    try {
      if (typeof obj === 'string') {
        return redactLogString(obj);
      }
      return typeof obj === 'object' && obj !== null
        ? JSON.stringify(this.sanitize(obj))
        : redactLogString(String(obj));
    } catch {
      return redactLogString(String(obj));
    }
  }

  /**
   * Sanitize data for remote logging
   * - Removes circular references
   * - Converts non-serializable objects to string representations
   * - Limits array/object sizes
   */
  public static sanitize(data: unknown): unknown {
    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data === 'string') {
      return redactLogString(data);
    }

    const seen = new WeakSet();
    const sanitizeObj = (obj: any, depth: number): any => {
      if (typeof obj === 'string') {
        return redactLogString(obj);
      }

      if (obj === null || typeof obj !== 'object') {
        return obj;
      }

      if (depth >= 8) {
        return '[MaxDepth]';
      }

      if (seen.has(obj)) {
        return '[Circular Reference]';
      }

      seen.add(obj);

      // Handle special object types
      if (obj instanceof Error) {
        return {
          __type: 'Error',
          name: redactLogString(obj.name),
          message: redactLogString(obj.message),
          stack: obj.stack?.split('\n').slice(0, 10).map((line: string) => redactLogString(line))
        };
      }

      if (obj instanceof Date) {
        return { __type: 'Date', value: obj.toISOString() };
      }

      if (obj instanceof Map) {
        return { __type: 'Map', size: obj.size };
      }

      if (obj instanceof Set) {
        return { __type: 'Set', size: obj.size };
      }

      if (Array.isArray(obj)) {
        return obj.slice(0, 100).map((item) => sanitizeObj(item, depth + 1));
      }

      // Detect DOM elements
      if (typeof HTMLElement !== 'undefined' && obj instanceof HTMLElement) {
        return `[DOM Element: ${obj.tagName}${obj.id ? '#' + obj.id : ''}]`;
      }

      // Detect React elements
      if (obj.$$typeof || obj._owner || (obj.props && obj.type)) {
        return '[React Element]';
      }

      // Detect Phaser objects
      if (obj.scene || obj.texture || obj.anims) {
        return `[Phaser Object: ${obj.constructor?.name || 'Unknown'}]`;
      }

      const result: any = {};
      let count = 0;
      for (const key in obj) {
        if (count >= 50) {
          result['__truncated'] = 'Object has more than 50 properties';
          break;
        }
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          try {
            result[key] = isSensitiveLogKey(key)
              ? REDACTED_LOG_VALUE
              : sanitizeObj(obj[key], depth + 1);
          } catch (e) {
            result[key] = `[Error serializing: ${redactLogString(String(e))}]`;
          }
        }
        count++;
      }

      return result;
    };

    try {
      return sanitizeObj(data, 0);
    } catch (e) {
      return `[Serialization Error: ${redactLogString(String(e))}]`;
    }
  }
}
